import React, { useEffect, useRef } from 'react';
import { useGlobalSearchSuggestions } from '../../hooks/useGlobalSearchSuggestions';
import { useVideoSearchInput } from '../../hooks/useVideoSearchInput';
import {
    buildVideoResultsRoute,
    buildVideoWatchRoute
} from '../../utils/search-routes';
import { buildSearchInput } from '../../utils/video-search';
import SearchDiscoveryContent from './SearchDiscoveryContent';
import VideoSearchBar from './VideoSearchBar';
import styles from './GlobalSearchOverlay.module.scss';

interface GlobalSearchOverlayProps {
    onClose: () => void;
    onNavigate: (route: string) => void;
}

const CONTEXTUAL_DISCOVERY_MIN_LENGTH = 2;

const GlobalSearchOverlay = ({ onClose, onNavigate }: GlobalSearchOverlayProps) => {
    const {
        inputValue,
        setInputValue,
        displayTags,
        showTagSkeletons,
        tagLoadFailed
    } = useVideoSearchInput();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const suggestions = useGlobalSearchSuggestions(inputValue);
    const trimmedInput = inputValue.trim();
    const isInputEmpty = trimmedInput.length === 0;
    const shouldUseContextualDiscovery = trimmedInput.length >= CONTEXTUAL_DISCOVERY_MIN_LENGTH;
    const moduleVideos = shouldUseContextualDiscovery ? suggestions.videos : [];
    const moduleTags = isInputEmpty
        ? displayTags
        : shouldUseContextualDiscovery
            ? suggestions.tags
            : [];
    const moduleFighters = shouldUseContextualDiscovery ? suggestions.fighters : [];
    const moduleEvents = shouldUseContextualDiscovery ? suggestions.events : [];
    const isModuleLoading = isInputEmpty
        ? showTagSkeletons && !tagLoadFailed
        : shouldUseContextualDiscovery && suggestions.status === 'loading';
    const isModuleError = isInputEmpty
        ? tagLoadFailed
        : shouldUseContextualDiscovery && suggestions.status === 'error';

    useEffect(() => {
        const focusTimer = window.setTimeout(() => {
            inputRef.current?.focus();
        }, 0);

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            window.clearTimeout(focusTimer);
            document.body.style.overflow = originalOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const navigateTo = (route: string) => {
        onNavigate(route);
    };

    const handleSubmit = () => {
        if (!trimmedInput) {
            return;
        }

        navigateTo(buildVideoResultsRoute(inputValue));
    };

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="global-search-title">
            <button
                type="button"
                className={styles.scrim}
                onClick={onClose}
                aria-label="Close search"
            />
            <section className={styles.panel}>
                <header className={styles.header}>
                    <div className={styles.titleRow}>
                        <h2 className={styles.title} id="global-search-title">Search Combat TV</h2>
                        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close search">
                            <span className="material-symbols-outlined" aria-hidden="true">close</span>
                        </button>
                    </div>
                </header>

                <div className={styles.results}>
                    <section className={styles.searchModule} aria-label="Search discovery">
                        <VideoSearchBar
                            value={inputValue}
                            onValueChange={setInputValue}
                            onSubmit={handleSubmit}
                            inputId="global-search-input"
                            inputRef={inputRef}
                            inputAriaLabel="Search Combat TV"
                            placeholder="Search fighters, events, tags, or videos"
                            disableSubmitWhenEmpty
                            showClearButton
                        />
                        <SearchDiscoveryContent
                            tags={moduleTags}
                            fighters={moduleFighters}
                            events={moduleEvents}
                            videos={moduleVideos}
                            isLoading={isModuleLoading}
                            isError={isModuleError}
                            isBelowMinimum={!isInputEmpty && !shouldUseContextualDiscovery}
                            showVideos={shouldUseContextualDiscovery}
                            showViewAll={shouldUseContextualDiscovery}
                            showNoResultsMessage={shouldUseContextualDiscovery}
                            railBreakpoint="1280"
                            mobileMetaFirst
                            emptyMessage="Explore popular tags or start typing to find fights, fighters, and events."
                            belowMinimumMessage="Keep typing to search Combat TV."
                            noResultsMessage="No matches yet. Try a fighter, event, or tag."
                            errorTitle="Live search suggestions are unavailable."
                            errorCopy={!isInputEmpty ? `You can still view all results for "${trimmedInput}".` : undefined}
                            errorActionLabel={!isInputEmpty ? 'View all results' : undefined}
                            onVideoSelect={(video) => navigateTo(buildVideoWatchRoute(video))}
                            onTagSelect={(tag) => setInputValue(buildSearchInput('', [tag.name]))}
                            onFighterSelect={(fighter) => setInputValue(fighter)}
                            onEventSelect={(eventName) => setInputValue(eventName)}
                            onViewAll={() => navigateTo(buildVideoResultsRoute(inputValue))}
                        />
                    </section>
                </div>
            </section>
        </div>
    );
};

export default GlobalSearchOverlay;
